import { useMemo } from 'react';
import moment from 'moment';

import { DEFAULT_REQUEST_FORMAT } from '@/components/AgGrid/helpers/filterHelpers';
import type { AgDateCondition, AgDateFilterModel } from '@/components/AgGrid/components/AgDateFilter/types';

type Params = {
  conditions: ReadonlyArray<Pick<AgDateCondition, 'type' | 'dateFrom' | 'dateTo'>>;
  operator: 'and' | 'or';
};

type SubmittedCondition = Exclude<AgDateFilterModel, null | { conditions: unknown }>;

const format = (date: AgDateCondition['dateFrom']) => moment(date).format(DEFAULT_REQUEST_FORMAT);

// A month or year condition covers the whole period of its first date.
const toSubmitted = ({ type, dateFrom, dateTo }: Params['conditions'][number]): SubmittedCondition => {
  if (type === 'withInYear' || type === 'withInMonth') {
    const period = type === 'withInYear' ? 'year' : 'month';
    return {
      type,
      dateFrom: moment(dateFrom).startOf(period).format(DEFAULT_REQUEST_FORMAT),
      dateTo: moment(dateFrom).endOf(period).format(DEFAULT_REQUEST_FORMAT),
      filterType: 'date',
    };
  }
  return { type, dateFrom: format(dateFrom), dateTo: dateTo ? format(dateTo) : null, filterType: 'date' };
};

// The model the filter submits: both conditions with an upper-cased operator once both have a date, else the first.
export const useSubmitAgModel = ({ conditions, operator }: Params): Exclude<AgDateFilterModel, null> =>
  useMemo(() => {
    const submitted = conditions.map(toSubmitted);

    if (conditions.every(({ dateFrom }) => !!dateFrom)) {
      return { conditions: submitted, operator: operator === 'and' ? 'AND' : 'OR', filterType: 'date' };
    }

    return submitted[0];
  }, [conditions, operator]);
